/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './AcChargingSessions.css';


import { isNumber } from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../rmsReduxStore/rmsReduxHooks';
import { getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import { getAcDashboardReducer } from '../../rmsReduxStore/acReduxExports';
import { setAcFilterData } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import { formatNumberWithCommas, getValueInSeconds, useEffectOnce } from '../../../globalUtils/globalHooks';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { AlarmPulseLoader, SelectDropDown } from '../../../globalUtils/globalExports';
import { RectangularSkeleton } from '../../../globalUtils/SkeletonDesign/SkeletonDesign';

  interface ChargingSessionsProp {
    handleFilter: Function,
    getFilterValue: Function
  }

  const AcChargingSession: React.FC<ChargingSessionsProp> = (props) => {
  const { handleFilter, getFilterValue } = props;
  const { intervalRangeReferenceData } = useAppSelector(getReferenceDataReducer);
  const { newDashboardChargingSessionsInfo, newDashboardChargingSessionsStats} = useAppSelector(getAcDashboardReducer);
  
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const dispatch = useAppDispatch();

  const [activeIndex, setActiveIndex] = useState(0);
  const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
  const navigationUpperLimit = 31
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const [yAxisLabel, setYAxisLabel] = useState('Avg. Charging Session Time');
  const [ chartHeight, setChartHeight ] = useState(300);


  const dataLength = newDashboardChargingSessionsInfo?.length || 0;
  const isLoading = !newDashboardChargingSessionsStats;

  const options = [
    {
      'id': 'sessionCount',
      'value': 'sessionCount',
      'label': 'sessionCount',
      'name': 'sessionCount'
    },
    {
      'id': 'averageSessionTime',
      'value': 'averageSessionTime',
      'label': 'averageSessionTime',
      'name': 'averageSessionTime'
    },
    {
      'id': 'energyConsumed',
      'value': 'energyConsumed',
      'label': 'energyConsumedr',
      'name': 'energyConsumed'
    },
  ]

  const handleChargingSessionFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
    const res = await setAcFilterData(val, filterKey, 'acdashboard', 'SET', false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
        handleFilter(res?.filters);
    }
  }, [setAcFilterData])

  useEffectOnce(() => {
    setFilterValue(getFilterValue('sessionIntervalRange'))
    const fetchTypeValues = getFilterValue('chargingSessionFetchType')?.[0]?.value || 'energyConsumed';
    const ind = options.findIndex((item) => item?.value === fetchTypeValues);
    handleClick(ind, true);
    setActiveIndex(ind);
  })

  useEffect(() => {
      const targetElement = document.getElementsByClassName('charging-session-highchart-wrap');
  
      if (!targetElement?.[0]) return;
  
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { height } = entry.contentRect;
          setChartHeight(Math.min(height, 400));
        }
      });
  
      resizeObserver.observe(targetElement[0]);
  
      return () => {
        resizeObserver.disconnect();
      };
    }, []);

  const handleSelectChange = (val): void => {
    const cardValue = val?.label === 'All' ? 'TOTAL' : val?.value?.toString()?.toLocaleUpperCase()
    setFilterValue(val);
    handleChargingSessionFilter(val, 'sessionIntervalRange');
    handleChartPageReset();
  };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  }

  const getFormattedData = useCallback(() => {
    const data = newDashboardChargingSessionsInfo || [];
    const slicedData = data.slice(visibleMin, visibleMax + 1); // Include max index

    const xAxis = slicedData.map(item => item?.date);
    const yAxis = slicedData.map(item => isNumber(item?.data) ? item?.data || 0 : Number(item?.data));
    return { xAxis, yAxis };
  }, [newDashboardChargingSessionsInfo, visibleMin, visibleMax]);

  let labelStep = 1;  //draft
  const { xAxis } = getFormattedData();
  const totalBars = xAxis.length;
  if (totalBars > 20) labelStep = 4;
  else if (totalBars > 10) labelStep = 2;

const handleNavigation = (direction: 'left' | 'right') => {
  const step = navigationUpperLimit + 1;
  if (direction === 'left') {
      setVisibleMin(Math.max(visibleMin - step, 0));
      setVisibleMax(Math.max(visibleMax - step, step));
  } else {
      setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
      setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
  }
};

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const [day, month] = label.split(' ');
  
    const chartWidth = this.axis.chart.chartWidth;
    const numCategories = this.axis.categories?.length || 1;
    const approxCategoryWidth = chartWidth / numCategories;
  
    const shouldWrap = approxCategoryWidth > 0; // adjust threshold if needed
  
    return shouldWrap
      ? `<div style="text-align: center;">${day}<br>${month}</div>`
      : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
  }; 

  const formatDuration = (seconds: number,shortAbb = true) => {
    if (seconds === 0) return shortAbb ? '0 s' : '0 Sec';
  
    const units = [
      { label: 'Hour',shortAbb: ' Hr',  value: Math.floor((seconds) / 3600) },
      { label: 'Min',shortAbb: ' Min',   value: Math.floor((seconds % 3600) / 60) },
      { label: 'Sec', shortAbb: ' Sec',  value: Math.floor(seconds % 60) }
    ];

    const nonZeroUnits = units.filter(unit => unit.value > 0).slice(0, 2);

    return nonZeroUnits
      .map(unit => `${unit.value}${shortAbb? unit?.shortAbb : (' ' + unit.label)}${(unit.value) > 1 && !shortAbb ? 's' : ''}`)
      .join(' ');
  }

  const formatTimeData = (val: number) => {
    const valInSeconds = getValueInSeconds(val, 'h');
    const formattedData = formatDuration(valInSeconds);
    return formattedData;
  }

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'spline',
      backgroundColor: 'transparent',
      height: chartHeight,
      spacing: [10, 0, 0, 10],
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;
  
          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }
  
          // Left arrow (shifted more inside by changing left position)
          if (visibleMin > 0) {
            chart.renderer.image(
              '/images/left_arrow_highchart.svg',
              chart.plotLeft, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow left')
            .on('click', () => handleNavigation('left'))
            .add();
          }
  
          // Right arrow (shifted more inside by changing left position)
          if (visibleMax < dataLength - 1) {
            chart.renderer.image(
              '/images/right_arrow_highchart.svg',
              chart.plotLeft + chart.plotWidth - 30, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow right')
            .on('click', () => handleNavigation('right'))
            .add();
          }
        }
      }
    },
    title: { text: undefined },
    xAxis: {
      categories: getFormattedData()?.xAxis,
      lineColor: '#03BFC0',
      tickColor: '#03BFC0',
      labels: {
        rotation: 0,
        // useHTML: true,
        step: labelStep,
        formatter: xAxisLabelFormatter,
      }
    },
    yAxis: {
      title: { text: yAxisLabel},
      // min: 0.5,
      // max: 2,
      gridLineDashStyle: 'ShortDash',
    },
    tooltip: {
      // valueSuffix:
      //   activeIndex === 0 ? ' sessions' :
      //   activeIndex === 1 ? ' hrs' :
      //   activeIndex === 2 ? ' MWh' : '',
      formatter: function () {
        if(activeIndex === 0) return `${this.x}</br>Sessions: <b>${this.y ?? 0}</b>`
        if(activeIndex === 1) return `${this.x}</br>Session Time: <b>${formatTimeData((this.y ?? 0)/60)}</b>`;
        if(activeIndex === 2) return `${this.x}</br>Energy Consumed: <b>${formatNumberWithCommas(Number(this.y ?? 0).toFixed(2))} KWh</b>`
      }
    },
    
    legend: { enabled: false },
    series: [
      {
        name: 'Session Time',
        type: 'spline',
        data: getFormattedData().yAxis,
        color: '#2EC4B6',
        lineWidth: 3,
        marker: {
          enabled: false,
        },
      },
    ],
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  const handleClick = (index: number, noFilterSet?: boolean) => {
    setActiveIndex(index);
    const obj = options[index];
    if(!noFilterSet) handleChargingSessionFilter(obj, 'chargingSessionFetchType');

    switch (index) {
      case 0:
        setYAxisLabel('No. of Charging Sessions');
        break;
      case 1:
        setYAxisLabel('Avg. Charging Session Time (Min)');
        break;
      case 2:
        setYAxisLabel('Total Energy Consumed (KWh)');
        break;
      default:
        setYAxisLabel('');
    }
    handleChartPageReset();
  };

  return (
    <div className="widget-wrap charging-session-card">
      <div className="widget-header chart-header">
        <div className='widget-heading charging-session-header'>
          Charging Sessions
          <IconWithTooltip iconClassName='info-icon' toolTipText={'Number of charging sessions, average session length, energy consumed, etc.'} containerClassName={'new-dashboard-widget-header-tooltip'} />
          {/* <p>No. of Charging Sessions, Average Charging Session Length, Energy Consumed, etc</p> */}
        </div>
        <div className="session-filter">
          <SelectDropDown
            selectValue={filterValue}
            selectOptions={intervalRangeReferenceData ?? []}
            handleSelectOptionChange={handleSelectChange}
            selectDropDownId='alarm-failed-transaction-dropdown'
            dropDownClassName='alarm-failed-transaction-dropdown'
            isMultiSelect={false}
            placeholder={'Interval'}
          />
        </div>
      </div>

      <div className='widget-body charging-session-body'>
        <div className="session-stats">
          {/* Charging Sessions */}
          <div
            className={`cursor-pointer session-stats-item ${activeIndex === 0 ? 'active' : ''}`}
            onClick={() => handleClick(0)}
          >
            <div className="session-stats-heading-charging-sessions">
              {isLoading
                ? <AlarmPulseLoader showLoader={true} />
                : formatNumberWithCommas(newDashboardChargingSessionsStats?.sessionCount ?? 0)}
            </div>
            <p style={{ color: activeIndex === 0 ? '#009E73' : '#666' }}>
              Charging Sessions
            </p>
          </div>

          {/* Avg. Session Length */}
          <div
            className={`cursor-pointer session-stats-item ${activeIndex === 1 ? 'active' : ''}`}
            onClick={() => handleClick(1)}
          >
            <div className="blue cursor-pointer">
              {isLoading
                ? <AlarmPulseLoader showLoader={true} />
                : formatTimeData((newDashboardChargingSessionsStats?.averageSessionTime ?? 0) / 60)}
            </div>
            <p style={{ color: activeIndex === 1 ? '#03BFC0' : '#666' }}>
              Avg. Charging<br />Session Length
            </p>
          </div>

          {/* Energy Consumed */}
          <div
            className={`cursor-pointer session-stats-item ${activeIndex === 2 ? 'active' : ''}`}
            onClick={() => handleClick(2)}
          >
          <div className="session-stats-heading-energy-consumed cursor-pointer">
            {isLoading ? (
              <AlarmPulseLoader showLoader={true} />
            ) : (
              (() => {
                const energyWh = newDashboardChargingSessionsStats?.energyConsumed ?? 0;

                if (energyWh < 1000) {
                  return `${formatNumberWithCommas(energyWh)} KWh`;
                } else {
                  const energyMWh = energyWh / 1000;
                  return `${formatNumberWithCommas(energyMWh.toFixed(2))} MWh`;
                }
              })()
            )}
          </div>
            <p style={{ color: activeIndex === 2 ? '#009E73' : '#666' }}>
              Energy Consumed
            </p>
          </div>
        </div>

        <div className='charging-session-highchart-wrap'>
          {!newDashboardChargingSessionsInfo ? (
            <div style={{ height: `${chartHeight}px` }}>
              <RectangularSkeleton skeletonContainerClassName='' />
            </div>
          ) : newDashboardChargingSessionsInfo?.length === 0 ? ( // or custom condition
            <div className='no-data-text'>
              No data available
            </div>
          ) : (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartRef}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default AcChargingSession;